export interface LibraryTimelineNavEvent {
  type: string;
  start: Date;
  end: Date;
}

export type LibraryTimelineNavDirection = 'next' | 'previous';
export type LibraryTimelineNavScope = 'frame' | 'event';

/**
 * Resolve the next/previous timeline jump target.
 * - frame: same event type as the active (or nearest) event
 * - event: a different event type
 * Falls back to nearest chronological neighbor when the playhead is not on a segment.
 */
export function resolveLibraryTimelineNavTarget(
  events: readonly LibraryTimelineNavEvent[],
  currentTime: Date,
  scope: LibraryTimelineNavScope,
  direction: LibraryTimelineNavDirection,
  activeType?: string | null,
): LibraryTimelineNavEvent | null {
  if (!events.length) {
    return null;
  }

  const currentMs = currentTime.getTime();
  if (!Number.isFinite(currentMs)) {
    return null;
  }

  const sorted = [...events].sort((a, b) => a.start.getTime() - b.start.getTime());
  const overlapping = sorted.filter(
    (event) => currentMs >= event.start.getTime() && currentMs <= event.end.getTime(),
  );
  const active =
    (activeType
      ? overlapping.find((event) => event.type === activeType) ?? overlapping.at(-1)
      : overlapping.at(-1)) ?? null;
  const typeFilter = scope === 'frame' ? (active?.type ?? activeType ?? null) : null;

  if (direction === 'next') {
    return (
      sorted.find((event) => {
        if (event.start.getTime() <= currentMs) {
          return false;
        }
        if (scope === 'frame') {
          return typeFilter ? event.type === typeFilter : true;
        }
        return active ? event.type !== active.type : true;
      }) ?? null
    );
  }

  return (
    [...sorted].reverse().find((event) => {
      if (event.start.getTime() >= currentMs) {
        return false;
      }
      if (scope === 'frame') {
        return typeFilter ? event.type === typeFilter : true;
      }
      return active ? event.type !== active.type : true;
    }) ?? null
  );
}

export function collectLibraryTimelineNavEvents(
  cameras: ReadonlyArray<{
    segments?: Record<string, Array<{ start: Date | string; end: Date | string }>>;
  }>,
  selectedTypes: readonly string[],
): LibraryTimelineNavEvent[] {
  const types = selectedTypes.length
    ? selectedTypes
    : Array.from(
        new Set(
          cameras.flatMap((camera) => Object.keys(camera.segments ?? {})),
        ),
      );
  const events: LibraryTimelineNavEvent[] = [];
  for (const camera of cameras) {
    for (const type of types) {
      for (const segment of camera.segments?.[type] ?? []) {
        const start = new Date(segment.start);
        const end = new Date(segment.end);
        if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) {
          continue;
        }
        events.push({ type, start, end });
      }
    }
  }
  return events.sort((a, b) => a.start.getTime() - b.start.getTime());
}
